import React from 'react';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Card } from '@uhg-abyss/web/ui/Card';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { styled } from '@uhg-abyss/web/tools/styled';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { useAuth } from '../../../auth';
import { SharedDialog } from '../../../common/SharedDialog';
import { useQuestionList } from './hooks/useQuestionList';
import { assignmentApi } from './api/assignmentApi';
import { DeleteQuestionDialog } from './DeleteQuestionDialog';
import { SurveyAssignmentDialog } from './SurveyAssignmentDialog';
import { navigate } from '../../../common/navigate';
import { PageSpinner } from '../../../common/PageSpinner';
import type { QuestionListItem, SurveySummary } from './types';
import { BRANCHING_ENABLED_TYPES } from './constants';

interface QuestionTableRow extends QuestionListItem {
  [key: string]: unknown;
}

const INPUT_LABEL_COLOR = '$web.semantic.color.text.content.primary';

const SearchInput = styled('input', {
  width: '100%',
  minWidth: '12rem',
  height: '2.5rem',
  borderRadius: '8px',
  border: '1px solid #b6c9d9',
  padding: '0 0.75rem',
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline:
      '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '1px',
  },
});

const selectInputCss = {
  'abyss-select-input-root': {
    width: '100%',
    minWidth: '12rem',
  },
  'abyss-select-input-label': {
    color: INPUT_LABEL_COLOR,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  'abyss-select-input-container': {
    minHeight: '2.5rem',
    borderRadius: '8px',
    border: '1px solid #b6c9d9',
    boxSizing: 'border-box' as const,
  },
  'abyss-select-input-selection-container': {
    minHeight: '2.5rem',
    padding: '0 0.75rem',
    fontSize: '0.95rem',
  },
} as const;

const Page = styled('section', {
  padding: '0',
  display: 'grid',
  gap: '1rem',
});

const PageHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  flexWrap: 'wrap',
});

const TableWrapper = styled('div', {
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  padding: '0 rem',
  backgroundColor: '$white',
  '& .abyss-data-table-root': {
    width: '100%',
    minWidth: 0,
  },
  '& table': {
    width: '100%',
    tableLayout: 'fixed',
  },
  '& th, & td': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  '@media (max-width: 1100px)': {
    padding: '0.375rem',
  },
  '@media (max-width: 900px)': {
    padding: '0.25rem',
  },
  '& .abyss-data-table-scroll': {
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  '& .abyss-data-table-header-cell-top-container': {
    alignItems: 'center',
  },
  '& .abyss-data-table-header-data': {
    display: 'flex',
    alignItems: 'center',
    minHeight: '1.5rem',
  },
  '& .abyss-data-table-header-actions-container': {
    alignItems: 'center',
    minHeight: '1.5rem',
    flexShrink: 0,
  },
  '& .abyss-data-table-header-sort-arrow-container': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const ActionGroup = styled('div', {
  display: 'flex',
  gap: '0.125rem',
  alignItems: 'center',
  justifyContent: 'flex-end',
  whiteSpace: 'nowrap',
});

const IconActionButton = styled(Button, {
  minWidth: '32px',
  width: '32px',
  height: '32px',
  padding: '0.25rem',
  '@media (max-width: 900px)': {
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: '0.15rem',
  },
});

const Badge = styled('span', {
  display: 'inline-block',
  padding: '0.15rem 0.5rem',
  borderRadius: '999px',
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: '#d1f0e0',
  color: '#1a7a45',
  variants: {
    inactive: {
      true: {
        backgroundColor: '#fde8e8',
        color: '#a02020',
      },
    },
  },
});

const FilterRow = styled('div', {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const SearchFieldWrapper = styled('div', {
  minWidth: '280px',
  flex: '1 1 320px',
});

const SurveyFilterWrapper = styled('div', {
  minWidth: '200px',
  flex: '0 1 260px',
});

const MobileList = styled('div', {
  display: 'grid',
  gap: '0.75rem',
});

const MobileQuestionCard = styled(Card, {
  padding: '1rem',
  display: 'grid',
  gap: '0.875rem',
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
});

const MobileFieldGrid = styled('div', {
  display: 'grid',
  gap: '0.75rem',
});

const MobileField = styled('div', {
  display: 'grid',
  gap: '0.15rem',
});

const MobileFieldLabel = styled(Text, {
  margin: 0,
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#345570',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
});

const ValueText = styled(Text, {
  margin: 0,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
});

const MobileActions = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
});

const TitleLink = styled('button', {
  display: 'block',
  width: '100%',
  minWidth: 0,
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  color: '$web.semantic.color.text.label.cta.primary',
  font: 'inherit',
  fontWeight: 600,
  textAlign: 'left',
  cursor: 'pointer',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  '&:focus-visible': {
    outline: '2px solid $web.semantic.color.surface.interactive.standards.rest.default.primary',
    outlineOffset: '2px',
  },
});

function getQuestionTypeName(typeId: number, questionType?: string | null): string {
  if (questionType && questionType.trim().length > 0) {
    return questionType;
  }

  const map: Record<number, string> = {
    1: 'Textbox', 2: 'Multiline', 3: 'Checkbox',
    4: 'Radio', 5: 'Numeric', 6: 'Alphanumeric', 7: 'Date',
  };
  return map[typeId] ?? String(typeId);
}

function getSurveyIdFromSearch(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = new URLSearchParams(window.location.search).get('surveyId');
  const n = v ? parseInt(v, 10) : NaN;
  return isNaN(n) ? undefined : n;
}

export const QuestionsListPage = () => {
  const urlSurveyId = React.useMemo(() => {return getSurveyIdFromSearch()}, []);
  const [filterSurveyId, setFilterSurveyId] = React.useState<number | ''>(urlSurveyId ?? '');
  const [filterSurveys, setFilterSurveys] = React.useState<SurveySummary[]>([]);
  const [filterSurveysLoading, setFilterSurveysLoading] = React.useState(false);
  const { questions, isLoading, error, remove, refresh } = useQuestionList(filterSurveyId || undefined);
  const { logout } = useAuth();
  const [viewportWidth, setViewportWidth] = React.useState(
    typeof window === 'undefined' ? 1280 : window.innerWidth,
  );

  const [search, setSearch] = React.useState('');
  const [searchDraft, setSearchDraft] = React.useState('');
  const [deleteTarget, setDeleteTarget] = React.useState<QuestionListItem | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [assignTarget, setAssignTarget] = React.useState<QuestionListItem | null>(null);

  // Branching survey picker — shown when navigating to branching from the all-questions view
  const [branchingTarget, setBranchingTarget] = React.useState<QuestionListItem | null>(null);
  const [branchingSurveys, setBranchingSurveys] = React.useState<SurveySummary[]>([]);
  const [branchingSurveyId, setBranchingSurveyId] = React.useState<number | ''>('');
  const [branchingSurveysLoading, setBranchingSurveysLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelledSurveys = false;
    setFilterSurveysLoading(true);
    assignmentApi.listSurveys()
      .then((res) => { if (!cancelledSurveys) setFilterSurveys(res.surveys.filter((s) => {return s.active})); })
      .catch(() => {})
      .finally(() => { if (!cancelledSurveys) setFilterSurveysLoading(false); });
    return () => { cancelledSurveys = true; };
  }, []);

  React.useEffect(() => {
    if (!branchingTarget) return;
    let cancelled = false;
    setBranchingSurveysLoading(true);
    assignmentApi.listSurveys()
      .then((res) => { if (!cancelled) setBranchingSurveys(res.surveys.filter((s) => {return s.active})); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setBranchingSurveysLoading(false); });
    return () => { cancelled = true; };
  }, [branchingTarget]);

  const handleBranchingNavigate = () => {
    if (!branchingTarget || !branchingSurveyId) return;
    navigate(`/admin/questions/${branchingTarget.questionId}/branching?surveyId=${branchingSurveyId}`);
  };

  const closeBranchingPicker = () => {
    setBranchingTarget(null);
    setBranchingSurveys([]);
    setBranchingSurveyId('');
  };

  const applySearch = React.useCallback(() => {
    setSearch(searchDraft);
  }, [searchDraft]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {return setViewportWidth(window.innerWidth)};

    window.addEventListener('resize', handleResize);
    return () => {return window.removeEventListener('resize', handleResize)};
  }, []);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return questions;
    return questions.filter(
      (item) =>
        {return item.questionTitle.toLowerCase().includes(q) ||
        (item.surveyQuestionDbCode ?? '').toLowerCase().includes(q)},
    );
  }, [questions, search]);

  const tableRows = React.useMemo<QuestionTableRow[]>(
    () => {return filtered.map((question) => {return { ...question }})},
    [filtered],
  );

  const isMobile = viewportWidth <= 900;

  const getActionButtons = React.useCallback((question: QuestionTableRow) => {return [
    {
      label: 'Edit question',
      icon: 'edit' as const,
      onClick: () => {return navigate(`/admin/questions/${question.questionId}/edit`)},
    },
    {
      label: 'Assign to survey',
      icon: 'assignment' as const,
      onClick: () => {return setAssignTarget(question)},
    },
    ...(BRANCHING_ENABLED_TYPES.has(question.questionTypeId)
      ? [{
          label: 'Configure branching',
          icon: 'account_tree' as const,
          onClick: () => {
            if (filterSurveyId) {
              // Already in a survey-scoped view — navigate directly
              navigate(`/admin/questions/${question.questionId}/branching?surveyId=${filterSurveyId}`);
            } else {
              // All-questions view — ask which survey to use
              setBranchingTarget(question);
              setBranchingSurveyId('');
            }
          },
        }]
      : []),
    {
      label: 'Delete question',
      icon: 'delete' as const,
      onClick: () => {return setDeleteTarget(question)},
    },
  ]}, [filterSurveyId]);

  const columns = React.useMemo(() => {
    const isCompact = viewportWidth <= 900;
    const isMedium = viewportWidth <= 1100;
    const isLargeCompact = viewportWidth <= 1400;
    const titleWidth = isCompact ? 150 : isMedium ? 180 : isLargeCompact ? 210 : 240;
    const titleMinWidth = isCompact ? 110 : isMedium ? 130 : isLargeCompact ? 150 : 180;
    const typeWidth = isCompact ? 84 : isMedium ? 92 : isLargeCompact ? 100 : 120;
    const dbCodeWidth = isCompact ? 100 : isMedium ? 115 : isLargeCompact ? 125 : 145;
    const requiredWidth = isCompact ? 68 : isMedium ? 78 : isLargeCompact ? 88 : 100;
    const statusWidth = isCompact ? 88 : isMedium ? 96 : isLargeCompact ? 106 : 118;
    const assignmentWidth = isCompact ? 140 : isMedium ? 170 : isLargeCompact ? 200 : 220;
    const orderWidth = isCompact ? 64 : isMedium ? 72 : isLargeCompact ? 80 : 92;
    const actionsWidth = isCompact ? 120 : isMedium ? 128 : 136;

    const baseColumns = [
      createColumn<QuestionTableRow>({
        id: 'questionTitle',
        accessorKey: 'questionTitle',
        header: 'Title',
        size: titleWidth,
        minSize: titleMinWidth,
        enableSorting: true,
        cell: (context: { row: { original: QuestionTableRow } }) => {return (
          <TitleLink
            type="button"
            onClick={() => {return navigate(`/admin/questions/${context.row.original.questionId}/edit`)}}
          >
            {context.row.original.questionTitle}
          </TitleLink>
        )},
      }),
      createColumn<QuestionTableRow>({
        id: 'questionTypeId',
        accessorKey: 'questionTypeId',
        header: 'Type',
        size: typeWidth,
        minSize: typeWidth,
        enableSorting: true,
        cell: (context: { row: { original: QuestionTableRow } }) => {return getQuestionTypeName(
          context.row.original.questionTypeId,
          context.row.original.questionType,
        )},
      }),
      createColumn<QuestionTableRow>({
        id: 'surveyQuestionDbCode',
        accessorKey: 'surveyQuestionDbCode',
        header: 'DB Code',
        size: dbCodeWidth,
        minSize: dbCodeWidth,
        enableSorting: true,
        cell: (context: { row: { original: QuestionTableRow } }) => {return context.row.original.surveyQuestionDbCode ?? '—'},
      }),
      createColumn<QuestionTableRow>({
        id: 'required',
        accessorKey: 'required',
        header: 'Required',
        size: requiredWidth,
        minSize: requiredWidth,
        enableSorting: true,
        cell: (context: { row: { original: QuestionTableRow } }) => {return (context.row.original.required ? 'Yes' : 'No')},
      }),
      createColumn<QuestionTableRow>({
        id: 'active',
        accessorKey: 'active',
        header: 'Status',
        size: statusWidth,
        minSize: statusWidth,
        enableSorting: true,
        cell: (context: { row: { original: QuestionTableRow } }) => {return (
          <Badge inactive={!context.row.original.active}>{context.row.original.active ? 'Active' : 'Inactive'}</Badge>
        )},
      }),
      createColumn<QuestionTableRow>({
        id: 'assignedSurveys',
        accessorKey: 'assignedSurveys',
        header: 'Surveys assigned',
        size: assignmentWidth,
        minSize: assignmentWidth,
        enableSorting: false,
        cell: (context: { row: { original: QuestionTableRow } }) => {
          const names = context.row.original.assignedSurveys;
          return names.length > 0
            ? <span title={names.join('\n')}>{names.join(', ')}</span>
            : <span style={{ color: '#999' }}>—</span>;
        },
      }),
    ];

    if (filterSurveyId) {
      baseColumns.push(
        createColumn<QuestionTableRow>({
          id: 'orderId',
          accessorKey: 'orderId',
          header: 'Order',
          size: orderWidth,
          minSize: orderWidth,
          enableSorting: true,
          cell: (context: { row: { original: QuestionTableRow } }) => {return context.row.original.orderId ?? '—'},
        }),
      );
    }

    baseColumns.push(
      createColumn<QuestionTableRow>({
        id: 'actions',
        accessorKey: 'questionId',
        header: 'Actions',
        size: actionsWidth,
        minSize: actionsWidth,
        enableSorting: false,
        cell: (context: { row: { original: QuestionTableRow } }) => {
          const question = context.row.original;
          const actionButtons = getActionButtons(question);

          return (
            <ActionGroup>
              {actionButtons.map((action) => {return (
                <Tooltip
                  key={action.label}
                  content={action.label}
                  position="top"
                  variant="gray"
                  delayDuration={200}
                >
                  <IconActionButton
                    variant="text"
                    size="sm"
                    aria-label={action.label}
                    title={action.label}
                    onClick={action.onClick}
                  >
                    <IconSymbol icon={action.icon} size={16} />
                  </IconActionButton>
                </Tooltip>
              )})}
            </ActionGroup>
          );
        },
      }),
    );

    return baseColumns;
  }, [getActionButtons, viewportWidth]);

  const tableState = useDataTable<QuestionTableRow>({
    initialData: tableRows,
    initialColumns: columns,
    rowIdKey: 'questionId',
    tableConfig: {
      enableSorting: true,
      enableColumnResizing: true,
      columnResizeMode: 'onEnd',
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [10, 20, 50],
    },
  });

  React.useEffect(() => {
    tableState.setData(tableRows);
  }, [tableRows, tableState]);

  React.useEffect(() => {
    tableState.setColumns(columns);
  }, [columns, tableState]);

  const visibleRows = ((tableState as unknown as { rows?: QuestionTableRow[] }).rows ?? tableRows) as QuestionTableRow[];

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await remove(deleteTarget.questionId);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  // 401 auth errors are shown as a blocking dialog and trigger logout on acknowledgement.
  const isAuthError = !!error && (error as Error & { status?: number }).status === 401;

  if (isAuthError) {
    return (
      <Page>
        <SharedDialog
          mode="error"
          isOpen={true}
          onClose={() => {}}
          title="Session expired"
          description="Your session has expired or is no longer valid. Please sign in again to continue."
          primaryAction={{ label: 'Sign in again', onClick: logout }}
          dismissible={false}
        />
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Text style={{ color: '#c0392b' }}>Failed to load questions: {error.message}</Text>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Heading level={1} style={{ margin: 0, fontSize: '1.4rem' }}>
          Questions{filterSurveyId ? ` — Survey ${filterSurveyId}` : ''}
        </Heading>
        <Button onClick={() => {return navigate('/admin/questions/new')}}>
          <IconSymbol icon="add" size={16} /> New question
        </Button>
      </PageHeader>

      <FilterRow>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '0 1 auto' }}>
          <SurveyFilterWrapper>
            <SelectInput
              id="questions-survey-filter"
              label="Filter by survey"
              hideLabel
              value={filterSurveyId}
              css={selectInputCss}
              onChange={(value) => {return setFilterSurveyId(value ? Number(value) : '')}}
              options={filterSurveys.map((s) => {return { label: s.name, value: s.surveyId }})}
              placeholder={filterSurveysLoading ? 'Loading surveys…' : 'All surveys'}
              isDisabled={filterSurveysLoading}
            />
          </SurveyFilterWrapper>
          {filterSurveyId && (
            <Button variant="outline" onClick={() => {return setFilterSurveyId('')}}>
              Clear
            </Button>
          )}
        </div>
        <SearchFieldWrapper>
          <SearchInput
            id="questions-search"
            type="text"
            placeholder="Search by title or DB code..."
            value={searchDraft}
            onChange={(e) => {return setSearchDraft(e.target.value)}}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applySearch();
              }
            }}
            aria-label="Search questions"
          />
        </SearchFieldWrapper>
        <Button variant="outline" onClick={applySearch}>
          Search
        </Button>
      </FilterRow>

      {isLoading ? (
      <PageSpinner label="Loading questions" />
      ) : filtered.length === 0 ? (
        <Text style={{ color: '#666', fontStyle: 'italic' }}>No questions found.</Text>
      ) : isMobile ? (
        <React.Fragment>
          <DataTable<QuestionTableRow>
            title={`Questions (${filtered.length})`}
            hideHeader
            tableState={tableState}
          >
            <DataTable.Pagination />
          </DataTable>
          <MobileList>
            {visibleRows.map((question) => {return (
              <MobileQuestionCard key={question.questionId}>
                <div>
                  <Heading level={2} style={{ margin: 0, fontSize: '1.05rem' }}>
                    {question.questionTitle}
                  </Heading>
                </div>
                <MobileFieldGrid>
                  <MobileField>
                    <MobileFieldLabel>Type</MobileFieldLabel>
                    <ValueText>{getQuestionTypeName(question.questionTypeId, question.questionType)}</ValueText>
                  </MobileField>
                  <MobileField>
                    <MobileFieldLabel>DB Code</MobileFieldLabel>
                    <ValueText>{question.surveyQuestionDbCode ?? '—'}</ValueText>
                  </MobileField>
                  <MobileField>
                    <MobileFieldLabel>Required</MobileFieldLabel>
                    <ValueText>{question.required ? 'Yes' : 'No'}</ValueText>
                  </MobileField>
                  <MobileField>
                    <MobileFieldLabel>Status</MobileFieldLabel>
                    <div>
                      <Badge inactive={!question.active}>{question.active ? 'Active' : 'Inactive'}</Badge>
                    </div>
                  </MobileField>
                  <MobileField>
                    <MobileFieldLabel>Surveys assigned</MobileFieldLabel>
                    <ValueText>{question.assignedSurveys.length > 0 ? question.assignedSurveys.join(', ') : '—'}</ValueText>
                  </MobileField>
                  {filterSurveyId ? (
                    <MobileField>
                      <MobileFieldLabel>Order</MobileFieldLabel>
                      <ValueText>{question.orderId ?? '—'}</ValueText>
                    </MobileField>
                  ) : null}
                </MobileFieldGrid>
                <MobileActions>
                  {getActionButtons(question).map((action) => {return (
                    <Button
                      key={action.label}
                      variant="outline"
                      size="sm"
                      aria-label={action.label}
                      onClick={action.onClick}
                    >
                      <IconSymbol icon={action.icon} size={16} /> {action.label}
                    </Button>
                  )})}
                </MobileActions>
              </MobileQuestionCard>
            )})}
          </MobileList>
        </React.Fragment>
      ) : (
        <TableWrapper>
          <DataTable<QuestionTableRow>
            title={`Questions (${filtered.length})`}
            hideHeader
            tableState={tableState}
          >
            <DataTable.Pagination />
            <DataTable.Table />
          </DataTable>
        </TableWrapper>
      )}

      <DeleteQuestionDialog
        questionTitle={deleteTarget?.questionTitle ?? ''}
        isOpen={!!deleteTarget}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {return setDeleteTarget(null)}}
      />

      {assignTarget && (
        <SurveyAssignmentDialog
          questionId={assignTarget.questionId}
          questionTitle={assignTarget.questionTitle}
          isOpen={true}
          onClose={() => { setAssignTarget(null); refresh(); }}
        />
      )}

      <SharedDialog
        mode="app"
        isOpen={!!branchingTarget}
        onClose={closeBranchingPicker}
        title="Select survey for branching"
        description={branchingTarget ? <React.Fragment><strong>&ldquo;{branchingTarget.questionTitle}&rdquo;</strong> — branching rules are scoped to a survey. Pick the survey you want to configure.</React.Fragment> : undefined}
        primaryAction={{
          label: 'Go to branching',
          disabled: !branchingSurveyId,
          onClick: handleBranchingNavigate,
        }}
        secondaryAction={{ label: 'Cancel', variant: 'outline', onClick: closeBranchingPicker }}
      >
        <SelectInput
          id="branching-survey-select"
          label="Survey"
          value={branchingSurveyId}
          css={selectInputCss}
          onChange={(value) => {return setBranchingSurveyId(value ? Number(value) : '')}}
          options={branchingSurveys.map((s) => {return { label: s.name, value: s.surveyId }})}
          placeholder={branchingSurveysLoading ? 'Loading surveys…' : 'Select a survey'}
          isDisabled={branchingSurveysLoading}
        />
      </SharedDialog>
    </Page>
  );
};
